import LegalPageLayout from "../components/LegalPageLayout";

const LAST_UPDATED = "February 11, 2026";

/**
 * Privacy Policy specific to order.screenzz.com (DIY ordering platform).
 * Intentionally NOT shared with the main marketing site at screenzz.com —
 * the platform handles payment + SMS scheduling + measurements, so it
 * needs disclosures the main site doesn't.
 *
 * The SMS section uses Twilio's expected toll-free verification language
 * almost verbatim; do not soften it without checking with their reviewers.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="This Privacy Policy applies to order.screenzz.com — the DIY ordering platform operated by ScreenzZ Window Screen Repair. It explains what information we collect when you place a custom screen door or window screen order, how we use it, and the choices you have."
      lastUpdated={LAST_UPDATED}
    >
      <Section title="1. Information We Collect">
        <p>When you use order.screenzz.com to place a custom order or request service, we may collect:</p>
        <ul>
          <li>Your name</li>
          <li>Phone number (mobile)</li>
          <li>Email address</li>
          <li>Billing and shipping address</li>
          <li>Measurements you submit for your custom screens or doors</li>
          <li>Order details — products selected, quantities, frame color, options, photos</li>
          <li>Device, browser, and basic analytics data (IP, referrer, pages visited)</li>
        </ul>
        <p>We collect this information directly from you when you submit it, and automatically when you browse the site.</p>
      </Section>

      <Section title="2. How We Use Your Information">
        <p>We use the information you provide to:</p>
        <ul>
          <li>Process your order and build your custom screens or doors to your specifications</li>
          <li>Provide customer support before, during, and after your order</li>
          <li>Communicate with you about your order via email, phone, or text message</li>
          <li>Schedule measurement appointments (when applicable)</li>
          <li>Coordinate pickup or delivery of your finished products</li>
          <li>Process payments securely through our payment processor</li>
          <li>Detect, prevent, and address fraud, abuse, or technical issues</li>
        </ul>
      </Section>

      <Section title="3. SMS / Text Messaging">
        <p>
          By providing your phone number, you consent to receive transactional text messages from ScreenzZ
          regarding orders, scheduling, measurement appointments, production updates, pickup notifications,
          delivery updates, and customer support.
        </p>
        <p>
          Message frequency varies depending on order activity. Message and data rates may apply. Reply
          <strong> STOP</strong> to opt out or <strong>HELP</strong> for assistance.
        </p>
        <p>
          <strong>Mobile phone information will not be shared, sold, or used for third-party marketing purposes.</strong>
        </p>
      </Section>

      <Section title="4. Payments">
        <p>
          Payments on order.screenzz.com are securely processed by <strong>Stripe</strong> and, in limited cases,
          <strong> Square</strong>. Card details are entered directly into the processor's PCI-compliant
          environment — ScreenzZ does not see, store, or have access to your full card number.
        </p>
      </Section>

      <Section title="5. Cookies & Analytics">
        <p>
          We use cookies and similar technologies to keep you signed in to your in-progress order, remember
          your preferences, and measure how the site performs. We use <strong>Google Analytics</strong> to
          understand which pages and features customers use, so we can improve the platform and customer
          experience. This data is aggregated and does not identify you personally to us.
        </p>
        <p>
          Most browsers let you control cookies through their settings. Blocking cookies may prevent parts
          of the order flow from working correctly.
        </p>
      </Section>

      <Section title="6. How We Share Information">
        <p>We share information only as needed to fulfill your order or as required by law:</p>
        <ul>
          <li>With our payment processors (Stripe, Square) to process your payment</li>
          <li>With our SMS and email providers (Twilio, Resend) to deliver order communications you've opted into</li>
          <li>With shipping or delivery partners when you choose delivery</li>
          <li>When required by law, subpoena, or to protect our rights or the safety of others</li>
        </ul>
        <p>We do not sell your information to third parties.</p>
      </Section>

      <Section title="7. Data Retention">
        <p>
          We keep order records, including customer contact information and measurements, for as long as
          needed to support warranty claims, accounting, tax, and legal obligations. You can request deletion
          of your account contact info by contacting us — order records required for tax and warranty
          purposes may be retained even after a deletion request.
        </p>
      </Section>

      <Section title="8. Children's Privacy">
        <p>
          order.screenzz.com is not directed at children under 13. We do not knowingly collect personal
          information from anyone under 13. If you believe a child has provided us with personal information,
          please contact us and we will delete it.
        </p>
      </Section>

      <Section title="9. Your Choices">
        <p>You can:</p>
        <ul>
          <li>Opt out of SMS by replying <strong>STOP</strong> to any text we send</li>
          <li>Unsubscribe from emails using the link in any marketing email</li>
          <li>Request a copy or deletion of your data by emailing us</li>
        </ul>
        <p>
          California residents have additional rights under the CCPA, including the right to know, delete,
          and opt out of sale (we do not sell personal information).
        </p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we'll update the &quot;Last
          updated&quot; date at the top of this page. Significant changes will be communicated by email or
          a prominent notice on the site.
        </p>
      </Section>
    </LegalPageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--brand-text)] mb-3">{title}</h2>
      <div className="text-[15px] leading-relaxed space-y-3 muted-text">{children}</div>
    </section>
  );
}
